import { TextAnchor, TextAnchorProps, Trigger, useFormatDate, useFormatDateTime } from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DataFieldIndexResource } from "../../../../shared/types/dataField";
import { useContactUpdateInquiry } from "../../contactUpdateInquiries/ContactUpdateInquiryProvider";
import {
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionListItem,
} from "../../shared/view";
import { ContactUpdateDrawer } from "./ContactUpdateDrawer";

const GeneralInfoContent = styled.div`
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: ${({ theme }) => theme.spacing(5)};
	flex: 1;
	justify-items: start;
`;

const Label = styled.span`
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const StyledAnchor = styled(TextAnchor)`
	font-size: inherit;
	text-transform: lowercase;
	white-space: nowrap;
	color: inherit;
	text-decoration: underline;
`;

const Row: FC<{ label: string; value: string }> = ({ label, value }) => {
	return (
		<>
			<Label>{label}:</Label>
			<span>{value}</span>
		</>
	);
};

interface ContactAnchorProps extends Omit<TextAnchorProps, "onClick"> {
	type: "mailto" | "tel";
}

export const ContactAnchor: FC<ContactAnchorProps> = ({ type, children }) => {
	return (
		<StyledAnchor onClick={(e) => e.stopPropagation()} href={`${type}:${children}`}>
			{children}
		</StyledAnchor>
	);
};

export const ContactViewGeneralSection: FC = () => {
	const { t } = useTranslation();
	const { dataFieldValues, setDataFieldValues, dataFields } = useContactUpdateInquiry();
	const formatDateTime = useFormatDateTime();
	const formatDate = useFormatDate();

	const resolveDataFieldValue = useCallback(
		(value: any, dataField: DataFieldIndexResource) => {
			switch (dataField?.type) {
				case "checkbox":
					return value ? t("Yes") : t("No");
				case "selectField":
				case "radioField":
					return dataField.options.find((option) => `${option.id}` === `${value}`)?.value;
				case "dateTime":
					return formatDateTime(value);
				case "date":
					return formatDate(value);
				case "checkboxGroup":
				case "multiSelectField": {
					const options =
						dataField.options && value ? dataField.options.filter((option) => value.includes(option.id)) : [];
					return options.map((option) => option.value).join(", ");
				}
				case "phone":
					return <ContactAnchor type="tel">{value}</ContactAnchor>;
				case "email":
					return <ContactAnchor type="mailto">{value}</ContactAnchor>;
				default:
					return value;
			}
		},
		[formatDate, formatDateTime, t],
	);

	if (!dataFields.length) return null;

	return (
		<ViewPageSection style={{ gridArea: "general" }}>
			<ViewPageSectionHeader>{t("General")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				<Trigger
					render={(props) => (
						<ContactUpdateDrawer
							{...props}
							onClose={props.handleClose}
							dataFields={dataFields}
							values={dataFieldValues}
							onSubmit={(values) => {
								setDataFieldValues(Object.keys(values).map((id) => ({ id: +id, value: values[id] })));
							}}
						/>
					)}
				>
					{(props) => (
						<ViewPageSectionListItem {...props}>
							<GeneralInfoContent>
								{dataFields.map((dataField) => {
									const dataFieldValue = dataFieldValues.find((value) => value.id === dataField.id);
									return (
										<Row
											key={dataField.id}
											label={dataField.label}
											value={dataFieldValue ? resolveDataFieldValue(dataFieldValue.value, dataField) : "-"}
										/>
									);
								})}
							</GeneralInfoContent>
						</ViewPageSectionListItem>
					)}
				</Trigger>
			</ViewPageSectionBody>
		</ViewPageSection>
	);
};
